/**
 * Filter Context
 * Provides global filter state for dashboard visibility logic
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { getDefaultFilter } from '@/utils/role-based-content';

interface FilterContextValue {
  currentFilter: string;
  setCurrentFilter: (filter: string) => void;
  isEveryoneFilter: boolean;
}

const FilterContext = createContext<FilterContextValue | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  
  // Initialize with default filter based on user role
  const [currentFilter, setCurrentFilter] = useState<string>(() => {
    return getDefaultFilter(user.role, user.id);
  });

  // Determine if current filter is "everyone"
  const isEveryoneFilter = currentFilter === 'everyone';

  // Debug logging
  useEffect(() => {
    console.log('[FilterContext] Current filter:', currentFilter);
    console.log('[FilterContext] Is everyone filter:', isEveryoneFilter);
  }, [currentFilter, isEveryoneFilter]);

  const value: FilterContextValue = {
    currentFilter,
    setCurrentFilter,
    isEveryoneFilter,
  };

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
}

export function useFilter() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
}
